import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { documentsApi, useGetDocumentDownloadVerdictsByIdsQuery } from "documents/_api/document-api";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import DocumentDownloadVerdict from "documents/downloads/document-download-verdict";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { useMemo } from "react";

/** How often a row still being scanned or processed is read again. */
export const WAITING_POLL_MS = 30_000;

/**
 * Whether a check that is still running holds this row: a scan in progress, or a classification
 * still to come after a clean scan. A failed classification, an infected file and a failed scan are
 * all final, and a row they hold would keep the whole batch polling for as long as the page is open.
 */
function isWaitingOnACheck(verdict: DocumentDownloadVerdict): boolean {
  if (verdict.scanStatus === DocumentFileScanStatus.Pending) return true;
  return verdict.scanStatus === DocumentFileScanStatus.Clean
    && verdict.contentAccess === DocumentContentAccess.Quarantined
    && verdict.classificationStatus === DocumentClassificationStatus.Pending;
}

/**
 * This reader's download verdict for each of these documents, keyed by id, for a page whose rows came
 * from somewhere else (#6992).
 *
 * A work agreement or proposal row is the work's projection of a document, and it carries no
 * classification, no content access and no scan status. The documents API answers those for this
 * reader with the same gates its download uses, so a row can be disabled only where the download
 * would be refused. It answers with verdicts rather than documents, because a document names its
 * creator, and the proposal decides which party may learn that (#6623). A document the API did not
 * answer for - refused or missing - has no entry, and its Download stays as it was: the server still
 * answers the click.
 */
export default function useDocumentDownloadVerdicts(
  documentIds: readonly (Guid | undefined)[],
): Map<string, DocumentDownloadVerdict> {
  // Sorted, so the same documents in another order are the same query.
  const ids = [...new Set(documentIds.map((id) => id?.value).filter((id): id is string => Boolean(id)))].sort();
  const key = ids.join(",");
  // A plain object, stable while the ids are: RTK Query keeps only plain values stable, and a new
  // argument on every render would be read again on every render.
  const argument = useMemo(() => (key ? { documentIds: key.split(",") } : skipToken), [key]);
  // A row withheld only until a check finishes is read again on its own: the server announces the
  // end of a scan and of a classification, but a lost announcement is not retried, and the tooltip
  // tells the reader to try again in a minute.
  const { data: cached } = documentsApi.endpoints.getDocumentDownloadVerdictsByIds.useQueryState(argument);
  const pollingInterval = cached?.some(isWaitingOnACheck) ? WAITING_POLL_MS : 0;
  const { data } = useGetDocumentDownloadVerdictsByIdsQuery(argument, { pollingInterval });

  return useMemo(() => new Map((data ?? []).map((verdict) => [verdict.id.value, verdict])), [data]);
}
